import { sanitize } from '@capsicum/common';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';

import { wcPledgeBasketLengthAtom, wcPledgeBasketLoadingAtom, wcPledgeBasketSelectedTeamIdsAtom } from '../data-access';
import { TeamRegionEnum, TokensTabFilters, type WCPledgeTeam } from '../models';

import { useWCPledgeBasketState } from './useWCPledgeBasketState';
import { useWCPledgeTeamsList } from './useWCPledgeTeamsList';
import { useWCPledgeTokensTabTexts } from './useWCPledgeTokensTabTexts';

export const useWCPledgeTokensTab = (
	searchText: string,
	selectedFilter: string,
	isShowAvailableOnly: boolean,
	setSelectedFilter: (filter: string) => void
) => {
	const isBasketLoading = useAtomValue(wcPledgeBasketLoadingAtom);
	const basketLength = useAtomValue(wcPledgeBasketLengthAtom);

	const selectedTeamIdsSet = useAtomValue(wcPledgeBasketSelectedTeamIdsAtom);
	const { maxTeamsPerPledge, toggleBasketSelection } = useWCPledgeBasketState();

	const { filtersTexts } = useWCPledgeTokensTabTexts();
	const { isLoading: isTeamsLoading, teamList } = useWCPledgeTeamsList();

	const filteredTeamList = useMemo(() => {
		let list = [...teamList];

		if (searchText)
			list = list.filter(team => team.name.toLowerCase().includes(sanitize(searchText).toLowerCase()));
		if (selectedFilter !== TokensTabFilters.All && selectedFilter) {
			list = list.filter(team => team.region === selectedFilter);
		}
		if (isShowAvailableOnly) {
			list = list.filter(team => team.canUserPledge && team.status !== 'Failed');
		}

		list = list.filter(team => team.status !== 'Partnered');

		return list;
	}, [teamList, searchText, selectedFilter, isShowAvailableOnly]);

	const filteredPartneredTeamList = useMemo(() => {
		let list = [...teamList];

		if (searchText)
			list = list.filter(team => team.name.toLowerCase().includes(sanitize(searchText).toLowerCase()));
		if (selectedFilter !== TokensTabFilters.All && selectedFilter) {
			list = list.filter(team => team.region === selectedFilter);
		}

		list = list.filter(team => team.status === 'Partnered');

		return list;
	}, [teamList, searchText, selectedFilter]);

	const handleTeamCheckboxChange = useCallback(
		(teamId: string, ftName: string) => () => toggleBasketSelection(teamId, ftName),
		[toggleBasketSelection]
	);

	const handleFilterChange = useCallback(
		(selected: Array<string>) => {
			if (selected[0] !== undefined) setSelectedFilter(selected[0]);
		},
		[setSelectedFilter]
	);

	const filters = useMemo(() => {
		const baseFilters = [
			{
				name: `${filtersTexts.all} (${teamList.length})`,
				testID: 'wcpledge-team-filter-all',
				value: TokensTabFilters.All
			}
			// { value: TokensTabFilters.Top10, name: filtersTexts.top10, testID: 'wcpledge-team-filter-top10' }
		];

		const regionalFilters = [
			{
				name: `${filtersTexts.africa} (${teamList.filter(team => team.region === TeamRegionEnum.Africa).length})`,
				testID: 'wcpledge-team-filter-africa',
				value: TokensTabFilters.Africa
			},
			{
				name: `${filtersTexts.asia} (${teamList.filter(team => team.region === TeamRegionEnum.Asia).length})`,
				testID: 'wcpledge-team-filter-asia',
				value: TokensTabFilters.Asia
			},
			{
				name: `${filtersTexts.europe} (${teamList.filter(team => team.region === TeamRegionEnum.Europe).length})`,
				testID: 'wcpledge-team-filter-europe',
				value: TokensTabFilters.Europe
			},
			{
				name: `${filtersTexts.southAmerica} (${teamList.filter(team => team.region === TeamRegionEnum.SouthAmerica).length})`,
				testID: 'wcpledge-team-filter-southAmerica',
				value: TokensTabFilters.SouthAmerica
			},
			{
				name: `${filtersTexts.northAmerica} (${teamList.filter(team => team.region === TeamRegionEnum.NorthAmerica).length})`,
				testID: 'wcpledge-country-filter-northAmerica',
				value: TokensTabFilters.NorthAmerica
			},
			{
				name: `${filtersTexts.oceania} (${teamList.filter(team => team.region === TeamRegionEnum.Oceania).length})`,
				testID: 'wcpledge-country-filter-oceania',
				value: TokensTabFilters.Oceania
			}
		];

		const filteredRegionalFilters = regionalFilters.filter(filter =>
			teamList.some(team => team.region === filter.value)
		);

		return [...baseFilters, ...filteredRegionalFilters];
	}, [teamList, filtersTexts]);

	const selectedTeamsInFilteredList = filteredTeamList.filter(team => selectedTeamIdsSet.has(team.id)).length;

	// Show how many teams in the filtered list are selected, out of the total filtered list
	const selectedTeamsCount = selectedTeamIdsSet.size;
	const selectedTeamsCountText = `${selectedTeamsCount}/${maxTeamsPerPledge}`;
	const isMaxTeamsPerPledgeReached = selectedTeamsCount >= maxTeamsPerPledge;

	const isLoading = isBasketLoading || isTeamsLoading;

	const isTeamCardDisabled = useCallback(
		(team: WCPledgeTeam) =>
			(isMaxTeamsPerPledgeReached && !selectedTeamIdsSet.has(team.id)) ||
			team.status === 'Failed' ||
			!team.canUserPledge,
		[isMaxTeamsPerPledgeReached, selectedTeamIdsSet]
	);

	return {
		basketLength,
		filteredPartneredTeamList,
		filteredTeamList,
		filters,
		handleFilterChange,
		handleTeamCheckboxChange,
		isLoading,
		isMaxTeamsPerPledgeReached,
		isTeamCardDisabled,
		maxTeamsPerPledge,
		selectedTeamIdsSet,
		selectedTeamsCountText,
		selectedTeamsInFilteredList
	};
};
